import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, ExternalLinkIcon, LightbulbIcon } from 'lucide-react';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SearchInput, SkeletonTable, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { FEEDBACK_KIND_LABELS, FEEDBACK_STATUSES, FEEDBACK_STATUS_LABELS, feedbackKindTone, feedbackStatusTone, listFeedback, updateFeedback, type Feedback } from '@/lib/feedback';

const SORT_FIELDS = { when: 'createdMs', kind: 'kindLabel', title: 'title', who: 'authorName', status: 'statusOrder' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.when, direction: 'desc' };
const STATUS_ORDER = { new: 0, seen: 1, done: 2 } as const;

interface FeedbackRow extends Feedback {
  readonly createdMs: number;
  readonly kindLabel: string;
  readonly statusOrder: number;
}
/** The row fields the search box matches. */
const FEEDBACK_SEARCH_FIELDS: ReadonlyArray<keyof FeedbackRow> = ['title', 'details', 'kindLabel', 'authorName', 'authorEmail', 'page'];

function feedbackRow(row: Feedback): FeedbackRow {
  return { ...row, createdMs: Date.parse(row.createdAt), kindLabel: FEEDBACK_KIND_LABELS[row.kind] ?? row.kind, statusOrder: STATUS_ORDER[row.status] ?? 0 };
}

/** Settings → Feedback: everything the team has filed, with its status and its Linear issue. Internal roles. */
export function FeedbackTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const feedback = useQuery({ queryKey: ['feedback'], queryFn: ({ signal }) => listFeedback(signal) });
  const patch = useMutation({
    mutationFn: (input: { id: string; status?: Feedback['status']; retryLinear?: boolean }) => updateFeedback(input.id, { ...(input.status ? { status: input.status } : {}), ...(input.retryLinear ? { retryLinear: true } : {}) }),
    onSuccess: async (_row, input) => {
      toast.success(input.retryLinear ? 'Sent to Linear again' : 'Updated');
      await queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (feedback.isPending) return <div className="mt-6"><SkeletonTable label="Loading feedback…" /></div>;
  if (feedback.isError) return <div className="mt-6"><ErrorNotice title="Feedback unavailable" message={errorMessage(feedback.error)} /></div>;
  const rows = sortRows(filterRows(feedback.data.map(feedbackRow), search, FEEDBACK_SEARCH_FIELDS), sort);

  return (
    <div className="mt-6">
      {feedback.data.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            label="Search feedback"
            placeholder="Title, details, kind, sender or page"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        </div>
      ) : null}
      {rows.length === 0 && feedback.data.length > 0 ? (
        <Panel>
          <EmptyState title="No feedback matches" message="Try a different search." />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState icon={<LightbulbIcon aria-hidden className="size-6" />} title="Nothing filed yet" message="Ideas, feedback and bug reports the team sends from the lightbulb in the header land here." />
        </Panel>
      ) : (
        <DataTable guide="settings-feedback-table" headers={[{ label: 'When', sortKey: SORT_FIELDS.when }, { label: 'Kind', sortKey: SORT_FIELDS.kind }, { label: 'Title', sortKey: SORT_FIELDS.title }, { label: 'From', sortKey: SORT_FIELDS.who }, 'Linear', { label: 'Status', sortKey: SORT_FIELDS.status }, 'Actions']} visuallyHiddenHeaders={[6]} sort={sort} onSort={onSort} minWidth={900}>
          {rows.map((row) => (
            <tr key={row._id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{new Date(row.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={feedbackKindTone(row.kind)}>{row.kindLabel}</Pill>
              </td>
              <td className="p-3 text-portal-small">
                <span className="block font-bold">{row.title}</span>
                <span className="block max-w-[420px] text-portal-micro text-portal-muted">{row.details.length > 160 ? `${row.details.slice(0, 160)}…` : row.details}</span>
                <span className="mt-1 flex flex-wrap gap-x-3 text-portal-micro">
                  {row.page ? (
                    <a href={row.page} className="text-brand hover:underline">
                      Page
                    </a>
                  ) : null}
                  {row.screenshotUrl ? (
                    <a href={row.screenshotUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                      Screenshot
                    </a>
                  ) : null}
                </span>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">
                {row.authorName ?? '—'}
                {row.authorEmail ? <span className="block text-portal-micro">{row.authorEmail}</span> : null}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                {row.linearIssueUrl ? (
                  <a href={row.linearIssueUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-brand hover:underline">
                    Open <ExternalLinkIcon aria-hidden className="size-3" />
                  </a>
                ) : row.linearError ? (
                  <span className="text-portal-micro text-rose-600" title={row.linearError}>
                    Not in Linear
                  </span>
                ) : (
                  <span className="text-portal-muted">—</span>
                )}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={feedbackStatusTone(row.status)}>{FEEDBACK_STATUS_LABELS[row.status]}</Pill>
              </td>
              <td className="p-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" aria-label={`Actions for ${row.title}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                      <EllipsisIcon aria-hidden className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {FEEDBACK_STATUSES.filter((status) => status !== row.status).map((status) => (
                      <DropdownMenuItem key={status} onSelect={() => patch.mutate({ id: row._id, status })}>
                        Mark {FEEDBACK_STATUS_LABELS[status].toLowerCase()}
                      </DropdownMenuItem>
                    ))}
                    {!row.linearIssueId ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => patch.mutate({ id: row._id, retryLinear: true })}>Send to Linear</DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
